import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "./cn"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-all avex:rounded-pill avex:font-semibold avex:shadow-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 avex:hover:bg-primary-hover",
        destructive:
          "bg-destructive text-white shadow-sm hover:bg-destructive/90 focus-visible:ring-destructive/20",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground avex:border-line-control avex:bg-transparent avex:hover:bg-surface-muted",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80 avex:border avex:border-line avex:bg-surface avex:text-ink avex:hover:border-line-strong avex:hover:bg-surface-muted",
        ghost: "hover:bg-accent hover:text-accent-foreground avex:text-ink-secondary avex:hover:bg-surface-sunken avex:hover:text-ink",
        link: "text-primary underline-offset-4 hover:underline avex:text-ink avex:hover:text-ink",
        // One entry of a list to pick from, such as the employees beside their
        // detail. Full width, text left, and `aria-pressed` marks the chosen one,
        // so the selected look is the kit's and not each module's.
        item: "h-auto w-full flex-col items-stretch gap-0 rounded-lg px-2 py-1.5 text-left font-normal text-gray-700 hover:bg-gray-50 aria-pressed:bg-gray-900 aria-pressed:text-white aria-pressed:hover:bg-gray-900 avex:rounded-avex-sm avex:font-normal avex:text-ink-secondary avex:hover:bg-surface-sunken avex:aria-pressed:bg-surface-inverse avex:aria-pressed:text-ink-inverse avex:aria-pressed:hover:bg-surface-inverse",
      },
      size: {
        default: "h-10 px-4 py-2 has-[>svg]:px-3",
        sm: "h-9 rounded-lg px-3 has-[>svg]:px-2.5 avex:h-8",
        // For actions inside a table row or beside a value: the design
        // system's xs button, 28px high.
        xs: "h-7 gap-1 rounded-lg px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-11 rounded-xl px-6 has-[>svg]:px-4",
        icon: "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="button"
      // A list entry sizes itself; the size scale is for actions.
      className={cn(buttonVariants({ variant, size: variant === "item" ? null : size }), className)}
      {...props}
    />
  )
}

export { Button, buttonVariants }
